import type {
  ISO8601DateOnly,
  ISO8601HoursAndMinutes,
  ISO8601Timestamp,
  Duration,
  ISO8601YearAndMonthOnly,
} from '../types/common.ts';
import { formatInTimeZone, toDate } from 'date-fns-tz';
import { INTEGRATION_TEST, UNIT_TEST } from './automated-testing.ts';

const testingCurrentTime = new Date('2023-07-20T00:22:45.550Z');
const testingOriginalMillis = testingCurrentTime.getTime();

/**
 * Sets the time that the tests think it is.
 */
export function testingUpdateDate(action: { reset: true } | { advance: Duration } | { date: Date }): Date {
  if (!UNIT_TEST && !INTEGRATION_TEST) {
    throw new Error('Do not call this function outside of tests!');
  }

  if ('reset' in action) {
    testingCurrentTime.setTime(testingOriginalMillis);
    return new Date(testingCurrentTime);
  } else if ('advance' in action) {
    const result = dateObjDurationAdd(testingCurrentTime, action.advance);
    testingCurrentTime.setTime(result.getTime());
    return result;
  } else {
    testingCurrentTime.setTime(action.date.getTime());
    return action.date;
  }
}

/**
 * All code should call this to get the current date so that we can control the time in our tests.
 */
export function dateNow(): Date {
  if (UNIT_TEST || INTEGRATION_TEST) {
    return new Date(testingCurrentTime);
  } else {
    return new Date();
  }
}

export function timestamp(date = dateNow()): ISO8601Timestamp {
  return date.toISOString() as ISO8601Timestamp;
}

export function timestampToDateOnly(timestamp: ISO8601Timestamp): ISO8601DateOnly {
  return timestamp.slice(0, 10) as ISO8601DateOnly;
}

type Hours =
  | 0
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12
  | 13
  | 14
  | 15
  | 16
  | 17
  | 18
  | 19
  | 20
  | 21
  | 22
  | 23;

export function timestampAtTimeInTimeZone(
  args: {
    date: ISO8601DateOnly;
    timeZone: string;
  } & (
    | { time: ISO8601HoursAndMinutes }
    | {
        hour: Hours;
        minute?: number;
      }
  ),
): ISO8601Timestamp {
  const { date, timeZone } = args;

  if ('time' in args) {
    return toDate(`${date}T${args.time}:00`, {
      timeZone,
    }).toISOString() as ISO8601Timestamp;
  } else {
    const { hour, minute = 0 } = args;
    return toDate(`${date}T${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:00`, {
      timeZone,
    }).toISOString() as ISO8601Timestamp;
  }
}

export function dateUtc(date = dateNow()): ISO8601DateOnly {
  return date.toISOString().slice(0, 10) as ISO8601DateOnly;
}

export function dateLocal(date: Date | ISO8601Timestamp = dateNow(), timeZone: string | null = null): ISO8601DateOnly {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (timeZone) {
    return formatInTimeZone(dateObj, timeZone, 'yyyy-MM-dd') as ISO8601DateOnly;
  }

  return `${dateObj.getFullYear()}-${(dateObj.getMonth() + 1).toString().padStart(2, '0')}-${dateObj
    .getDate()
    .toString()
    .padStart(2, '0')}` as ISO8601DateOnly;
}

/** Converts a date from an external system whose format we can't rely on into a calendar date in `timeZone`. Bare
 * dates and timestamps without an offset are already wall-clock dates, so only instants with an offset are converted.
 * Returns null when the value is missing or unparseable. */
export function dateLocalFromExternal(value: string | null | undefined, timeZone: string): ISO8601DateOnly | null {
  if (!value) {
    return null;
  }
  if (/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?)?$/.test(value)) {
    return value.slice(0, 10) as ISO8601DateOnly;
  }
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) {
    return null;
  }
  return dateLocal(date, timeZone);
}

export function startOfWeekUtc(date?: ISO8601DateOnly | ISO8601Timestamp): ISO8601DateOnly {
  const dateObj = new Date(date ?? dateNow());
  dateObj.setUTCHours(0, 0, 0, 0);
  while (dateObj.getUTCDay() !== 0) {
    dateObj.setUTCDate(dateObj.getUTCDate() - 1);
  }
  return dateObj.toISOString().slice(0, 10) as ISO8601DateOnly;
}

export function endOfWeekUtc(date?: ISO8601DateOnly | ISO8601Timestamp): ISO8601DateOnly {
  const dateObj = new Date(date ?? dateNow());
  dateObj.setUTCHours(0, 0, 0, 0);
  while (dateObj.getUTCDay() !== 6) {
    dateObj.setUTCDate(dateObj.getUTCDate() + 1);
  }
  return dateObj.toISOString().slice(0, 10) as ISO8601DateOnly;
}

export function startOfWeekLocal(date?: Date | ISO8601Timestamp): ISO8601DateOnly {
  let dateObj: Date;
  if (typeof date === 'string') {
    dateObj = new Date(date);
  } else if (!date) {
    dateObj = dateNow();
  } else {
    dateObj = new Date(date);
  }

  dateObj.setHours(0, 0, 0, 0);
  while (dateObj.getDay() !== 0) {
    dateObj.setDate(dateObj.getDate() - 1);
  }
  return dateLocal(dateObj, null);
}

export function dateObjDurationAdd(date: Date, duration: Duration): Date {
  const dateChanging = new Date(date);

  const months = duration.years * 12 + duration.months;
  if (months !== 0) {
    // Adding months and years is complicated by ensuring dates stay anchored to the end of the month. For example,
    // advancing Jan 31 should end up at Feb 28 or 29 instead of March 2 or 3.

    // First, find the # of days in the target month:
    const endOfDesiredMonth = new Date(date);
    // To do so, we overshoot by 1 month, but set day 0 which results in us being on the last day of the desired month:
    endOfDesiredMonth.setUTCMonth(endOfDesiredMonth.getUTCMonth() + months + 1, 0);
    // Then, we trivially have the total number of days in the month:
    const daysInMonth = endOfDesiredMonth.getUTCDate();

    // If the month has enough days, we can do trivial date math:
    if (daysInMonth >= date.getUTCDate()) {
      dateChanging.setUTCMonth(dateChanging.getUTCMonth() + months);
    } else {
      // Otherwise we go to the end of the month:
      dateChanging.setTime(endOfDesiredMonth.getTime());
    }

    if (duration.locationInMonth) {
      if (duration.locationInMonth === 31 || duration.locationInMonth === 30 || duration.locationInMonth === 29) {
        if (daysInMonth <= duration.locationInMonth) {
          dateChanging.setTime(endOfDesiredMonth.getTime());
        } else {
          dateChanging.setUTCDate(duration.locationInMonth);
        }
      } else if (duration.locationInMonth.position === 'last') {
        dateChanging.setTime(endOfDesiredMonth.getTime());
        while (dateChanging.getUTCDay() !== duration.locationInMonth.dayOfWeek) {
          dateChanging.setUTCDate(dateChanging.getUTCDate() - 1);
        }
      } else {
        let countsNeeded = ['first', 'second', 'third', 'fourth'].indexOf(duration.locationInMonth.position) + 1;
        dateChanging.setUTCDate(1);
        while (countsNeeded > 0) {
          if (dateChanging.getUTCDay() === duration.locationInMonth.dayOfWeek) {
            countsNeeded -= 1;
            if (countsNeeded === 0) {
              break;
            }
          }

          dateChanging.setUTCDate(dateChanging.getUTCDate() + 1);
        }
      }

      if (duration.weeks !== 0 || duration.days !== 0) {
        console.error(
          'Duration is ambiguous and likely incorrect. locationInMonth is specified, but then the date is adjusted by weeks and days. We will skip the weeks and days.',
          duration,
        );
        duration = { ...duration, weeks: 0, days: 0 };
      }
    }
  } else if (duration.locationInMonth) {
    console.error(
      'Duration is ambiguous and likely incorrect. locationInMonth is specified, but no duration in years or months was provided. We will skip locationInMonth.',
      duration,
    );
  }

  if (duration.weeks !== 0 && duration.locationInWeek && duration.locationInWeek.daysOfWeek.length > 0) {
    const daysOfWeek = duration.locationInWeek.daysOfWeek.slice().sort((a, b) => a - b);
    if (duration.weeks < 0) {
      daysOfWeek.reverse();
    }
    const index = daysOfWeek.indexOf(dateChanging.getUTCDay() as (typeof duration.locationInWeek.daysOfWeek)[number]);
    const advanceDirection = duration.weeks < 0 ? -1 : 1;
    if (index === -1) {
      // If we're not aligned to daysOfWeek, walk the date day by day until we are:
      const daysOfWeekSet = new Set(daysOfWeek);
      while (!daysOfWeekSet.has(dateChanging.getUTCDay() as (typeof duration.locationInWeek.daysOfWeek)[number])) {
        dateChanging.setUTCDate(dateChanging.getUTCDate() + advanceDirection);
      }
    } else {
      // If we are on a proper day, find the next day:
      let nextValidDay = daysOfWeek[index + 1];
      let jump = 0;

      // If we can't find the next day for this week, we must need to go to the first day of next week:
      if (nextValidDay === undefined) {
        nextValidDay = daysOfWeek[0];
        // If abs(weeks) > 1, we'll need to add weeks in addition to seeking to the next day. Seeking to the next
        // valid day will already take care of 1 week change, so reduce the amount of weeks to jump by 1:
        jump = duration.weeks + -advanceDirection;
      }

      // Move at least 1 day:
      dateChanging.setUTCDate(dateChanging.getUTCDate() + advanceDirection);

      // And keep moving until we're on the next valid day of week:
      while (dateChanging.getUTCDay() !== nextValidDay) {
        dateChanging.setUTCDate(dateChanging.getUTCDate() + advanceDirection);
      }

      // Jump weeks if needed:
      if (jump !== 0) {
        dateChanging.setUTCDate(dateChanging.getUTCDate() + jump * 7);
      }
    }

    if (duration.days !== 0) {
      console.error(
        'Duration is ambiguous and likely incorrect. locationInWeek is specified, but so is days. We will skip days.',
        duration.locationInWeek,
      );
    }
    if (duration.months !== 0 || duration.months !== 0) {
      console.error(
        'Duration is ambiguous and likely incorrect. locationInWeek is specified, but so is months or years. We will honor months and years and locationInWeek, but this behavior is likely unexpected.',
        duration,
      );
    }
  } else {
    if (duration.locationInWeek && duration.weeks === 0) {
      console.error(
        'Duration is ambiguous and likely incorrect. locationInWeek is specified, but weeks is not. We will skip locationInWeek.',
        duration,
      );
    }

    const days = duration.weeks * 7 + duration.days;
    if (days !== 0) {
      dateChanging.setUTCDate(dateChanging.getUTCDate() + days);
    }
  }

  return dateChanging;
}

export function dateStrDurationAdd(date: ISO8601DateOnly, duration: Duration): ISO8601DateOnly {
  return dateUtc(dateObjDurationAdd(new Date(date), duration));
}

export function timestampStrDurationAdd(date: ISO8601Timestamp, duration: Duration): ISO8601Timestamp {
  return timestamp(dateObjDurationAdd(new Date(date), duration));
}

export function timestampStrHoursAdd(timestamp: ISO8601Timestamp, hours: number): ISO8601Timestamp {
  const date = new Date(timestamp);
  date.setUTCHours(date.getUTCHours() + hours);
  return date.toISOString() as ISO8601Timestamp;
}

export function timestampStrMinutesAdd(timestamp: ISO8601Timestamp, minutes: number): ISO8601Timestamp {
  const date = new Date(timestamp);
  date.setUTCMinutes(date.getUTCMinutes() + minutes);
  return date.toISOString() as ISO8601Timestamp;
}

/**
 * Shorthand for creating a duration without specifying all fields.
 */
export function duration(fields: Partial<Duration> = {}): Duration {
  return { years: 0, months: 0, weeks: 0, days: 0, locationInWeek: null, locationInMonth: null, ...fields };
}

/**
 * Returns a copy of the duration with the numerical fields negated.
 */
export function durationInvert(duration: Duration): Duration {
  return {
    years: -duration.years,
    months: -duration.months,
    weeks: -duration.weeks,
    days: -duration.days,
    locationInWeek: duration.locationInWeek,
    locationInMonth: duration.locationInMonth,
  };
}

/**
 * Returns a number between 0-1 representing how far we are between two dates. If "now" is past the start or end, it
 * is clamped to 0 or 1 respectively.
 */
export function percentageBetweenTwoDates({ start, end, now }: { start: Date; end: Date; now: Date }): number {
  if (end.getTime() === start.getTime()) {
    console.error('percentageBetweenTwoDates: start and end are identical.', { start, end, now });
    return 0;
  }

  if (end < start) {
    console.error('percentageBetweenTwoDates: start and end were swapped.', { start, end, now });
    return 0;
  }

  if (now <= start) {
    return 0;
  }

  if (now >= end) {
    return 1;
  }

  const startEndDurationInMillis = end.getTime() - start.getTime();

  if (startEndDurationInMillis <= 0) {
    console.error('percentageBetweenTwoDates: diff is nonsensical', { start, end, now, startEndDurationInMillis });
    return 0;
  }

  const elapsedToNowInMillis = end.getTime() - now.getTime();

  return 1 - elapsedToNowInMillis / startEndDurationInMillis;
}

/**
 * Returns true if the years, months, weeks, and days of a Duration are all zero. locationInWeek and locationInMonth
 * are ignored as any values specified would be nonsensical.
 */
export function durationIsZero(duration: Duration): boolean {
  return duration.years === 0 && duration.months === 0 && duration.weeks === 0 && duration.days === 0;
}

/**
 * Returns true if all components of a duration are non-negative, and at least 1 component is positive.
 *
 * Primarily useful when validating Durations that should always advance forward (i.e. notification frequency).
 */
export function durationIsEntirelyPositive(duration: Duration): boolean {
  if (duration.years < 0 || duration.months < 0 || duration.weeks < 0 || duration.days < 0) {
    return false;
  }

  if (duration.years === 0 && duration.months === 0 && duration.weeks === 0 && duration.days === 0) {
    return false;
  }

  return true;
}

/**
 * Returns the user's current time zone. This should work in every modern browser, but we fall back to New York just in
 * case.
 */
export function ianaTimeZoneCurrentGet() {
  return new Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'America/New_York';
}

export function dateToLocaleDateString(date: ISO8601DateOnly, options?: Intl.DateTimeFormatOptions): string {
  const [year, month, day] = date.split('-');
  return new Date(Number(year), Number(month) - 1, Number(day), 0, 0, 0, 0).toLocaleDateString(undefined, options);
}

export function dateToDayOfWeek(date: ISO8601DateOnly): string {
  const [year, month, day] = date.split('-');
  return new Date(Number(year), Number(month) - 1, Number(day), 0, 0, 0, 0).toLocaleDateString(undefined, {
    weekday: 'long',
  });
}

export function timestampToLocaleString(
  timestamp: ISO8601Timestamp,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'short', timeStyle: 'short' },
): string {
  return new Date(timestamp).toLocaleString(undefined, options);
}

export function timestampToLocaleDateString(timestamp: ISO8601Timestamp): string {
  return new Date(timestamp).toLocaleString(undefined, { dateStyle: 'short' });
}

const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function durationToEnglish(duration: Duration, negate = false): string {
  const pieces: string[] = [];

  if (negate) {
    duration = {
      ...duration,
      years: -duration.years,
      months: -duration.months,
      weeks: -duration.weeks,
      days: -duration.days,
    };
  }

  if (duration.years !== 0) {
    pieces.push(`${duration.years} year${duration.years > 1 ? 's' : ''}`);
  }

  if (duration.months !== 0) {
    pieces.push(`${duration.months} month${duration.months > 1 ? 's' : ''}`);
  }

  if (duration.weeks !== 0) {
    pieces.push(`${duration.weeks} week${duration.weeks > 1 ? 's' : ''}`);
  }

  if (duration.days !== 0) {
    pieces.push(`${duration.days} day${duration.days > 1 ? 's' : ''}`);
  }

  if (duration.locationInWeek && duration.locationInWeek.daysOfWeek.length > 0) {
    pieces.push(`on ${duration.locationInWeek.daysOfWeek.map((index) => dayNames[index]).join(', ')}`);
  }

  if (duration.locationInMonth) {
    if (duration.locationInMonth === 29 || duration.locationInMonth === 30 || duration.locationInMonth === 31) {
      pieces.push(`on day ${duration.locationInMonth} of the month`);
    } else {
      pieces.push(
        `on the ${duration.locationInMonth.position} ${dayNames[duration.locationInMonth.dayOfWeek]} of the month`,
      );
    }
  }

  return pieces.join(', ');
}

export function durationToEnglishRelativeToDueDate(activates: Duration) {
  if (activates.years === 0 && activates.months === 0 && activates.weeks === 0 && activates.days === 0) {
    return 'the due date';
  } else if (durationIsEntirelyPositive(activates)) {
    return durationToEnglish(activates) + ' after the due date';
  } else {
    return durationToEnglish(activates, true) + ' before the due date';
  }
}

export function getSimpleDurationLengthInDays(duration: Duration): number {
  return 365 * duration.years + 30 * duration.months + 7 * duration.weeks + duration.days;
}

export function yearAndMonthFriendly(yearAndMonth: ISO8601YearAndMonthOnly) {
  const date = new Date(yearAndMonth + '-01');
  return date.toLocaleString(undefined, { timeZone: 'UTC', month: 'long', year: 'numeric' });
}

export function dateToYearAndMonth(date: ISO8601DateOnly | ISO8601Timestamp): ISO8601YearAndMonthOnly {
  return date.slice(0, 7) as ISO8601YearAndMonthOnly;
}

export function dateToFirstOfMonth(
  date: ISO8601DateOnly | ISO8601YearAndMonthOnly | ISO8601Timestamp,
): ISO8601DateOnly {
  return `${date.slice(0, 7)}-01` as ISO8601DateOnly;
}

export function dateToEndOfMonth(date: ISO8601DateOnly | ISO8601YearAndMonthOnly | ISO8601Timestamp): ISO8601DateOnly {
  return dateStrDurationAdd(
    dateStrDurationAdd(`${date.slice(0, 7)}-01` as ISO8601DateOnly, duration({ months: 1 })),
    duration({ days: -1 }),
  );
}

export function dateToWeekStart(dateStr: ISO8601DateOnly): ISO8601DateOnly {
  const date = new Date(dateStr);
  while (date.getUTCDay() !== 0) {
    date.setUTCDate(date.getUTCDate() - 1);
  }

  return dateUtc(date);
}

export function dateToWeekEnd(dateStr: ISO8601DateOnly): ISO8601DateOnly {
  const date = new Date(dateStr);
  while (date.getUTCDay() !== 6) {
    date.setUTCDate(date.getUTCDate() + 1);
  }

  return dateUtc(date);
}

export function dateToMonthStart(dateStr: ISO8601DateOnly): ISO8601DateOnly {
  const date = new Date(dateStr);
  date.setUTCDate(1);
  return dateUtc(date);
}

export function dateToMonthEnd(dateStr: ISO8601DateOnly): ISO8601DateOnly {
  const date = new Date(dateStr);
  date.setUTCDate(1);
  date.setUTCMonth(date.getUTCMonth() + 1);
  date.setUTCDate(0);
  return dateUtc(date);
}

const monthToQuarter = {
  0: 1,
  1: 1,
  2: 1,
  3: 2,
  4: 2,
  5: 2,
  6: 3,
  7: 3,
  8: 3,
  9: 4,
  10: 4,
  11: 4,
} as const;

export function dateToQuarterStart(dateStr: ISO8601DateOnly): ISO8601DateOnly {
  const date = new Date(dateStr);
  const quarter = monthToQuarter[date.getUTCMonth() as keyof typeof monthToQuarter];
  date.setUTCDate(1);
  date.setUTCMonth(quarter * 3 - 3);
  return dateUtc(date);
}

export function dateToQuarterEnd(dateStr: ISO8601DateOnly): ISO8601DateOnly {
  const date = new Date(dateStr);
  const quarter = monthToQuarter[date.getUTCMonth() as keyof typeof monthToQuarter];
  date.setUTCDate(1);
  date.setUTCMonth(quarter * 3);
  date.setUTCDate(0);
  return dateUtc(date);
}

export function dateToHalfYearStart(dateStr: ISO8601DateOnly): ISO8601DateOnly {
  const date = new Date(dateStr);
  date.setUTCDate(1);
  date.setUTCMonth(date.getUTCMonth() < 6 ? 0 : 6);
  return dateUtc(date);
}

export function dateToHalfYearEnd(dateStr: ISO8601DateOnly): ISO8601DateOnly {
  const date = new Date(dateStr);
  date.setUTCDate(1);
  date.setUTCMonth(date.getUTCMonth() < 6 ? 6 : 12);
  date.setUTCDate(0);
  return dateUtc(date);
}

export function dateToYear(dateStr: ISO8601DateOnly): number {
  return Number(dateStr.slice(0, 4));
}

export function dateToYearStart(dateStr: ISO8601DateOnly): ISO8601DateOnly {
  const date = new Date(dateStr);
  date.setUTCDate(1);
  date.setUTCMonth(0);
  return dateUtc(date);
}

export function dateToYearEnd(dateStr: ISO8601DateOnly): ISO8601DateOnly {
  const date = new Date(dateStr);
  date.setUTCDate(1);
  date.setUTCMonth(11);
  date.setUTCDate(31);
  return dateUtc(date);
}

export function hoursAndMinutesShift(
  time: ISO8601HoursAndMinutes,
  hours: number,
  minutes: number = 0,
): ISO8601HoursAndMinutes {
  const initial = new Date(`2024-01-01T${time}:00.000Z`);
  initial.setUTCHours(initial.getUTCHours() + hours);
  initial.setUTCMinutes(initial.getUTCMinutes() + minutes);
  return initial.toISOString().slice(11, 16) as ISO8601HoursAndMinutes;
}

export function hoursAndMinutesFriendly(time: ISO8601HoursAndMinutes): string {
  return new Date(`2024-01-01T${time}:00.000Z`).toLocaleTimeString(undefined, { timeStyle: 'short', timeZone: 'UTC' });
}

export function dueAtDateAndHoursToTimestamp(
  dueAt: ISO8601DateOnly,
  dueAtTime: ISO8601HoursAndMinutes,
  timeZone: string,
): ISO8601Timestamp {
  return timestampAtTimeInTimeZone({
    date: dueAt,
    time: dueAtTime,
    timeZone,
  });
}

export function dateDiffMs(a: ISO8601DateOnly, b: ISO8601DateOnly): number {
  return new Date(a).getTime() - new Date(b).getTime();
}

// In practice these fallbacks should never be used (i.e. most if not all usages are us failing to find facility data,
// which should not happen), but we need them to satisfy the type system. The justifications for both are:
// - Frontend = User's current time zone. In cases where this fallback could imaginably matter, it will be least
//   confusing for the user if they see things in their own time zone. Since many users are _at_ the facility
//   physically, this is also probably the most correct possible fallback.
// - Backend = New York time. Almost all of our customers are in the US, and quite a few of those customers have
//   facilities and even sometimes headquarters on the east coast.
export const TIME_ZONE_FRONTEND_FALLBACK = ianaTimeZoneCurrentGet();
export const TIME_ZONE_BACKEND_FALLBACK = 'America/New_York';
